type AutoplaySubscriber = (isActiveOwner: boolean) => void;

type AutoplayMember = {
    priority: number;
    visible: boolean;
    subscriber: AutoplaySubscriber;
};

const MAX_ACTIVE_AUTOPLAY_CANDIDATES = 3;
const autoplayGroups = new Map<string, Map<string, AutoplayMember>>();

function getOrCreateGroup(groupId: string) {
    let group = autoplayGroups.get(groupId);

    if (!group) {
        group = new Map<string, AutoplayMember>();
        autoplayGroups.set(groupId, group);
    }

    return group;
}

function resolveActiveOwnerIds(groupId: string) {
    const group = autoplayGroups.get(groupId);

    if (!group) {
        return new Set<string>();
    }

    return new Set(
        [...group.entries()]
            .filter(([, member]) => member.visible)
            .sort(([, left], [, right]) => left.priority - right.priority)
            .slice(0, MAX_ACTIVE_AUTOPLAY_CANDIDATES)
            .map(([ownerId]) => ownerId),
    );
}

function notifyGroup(groupId: string) {
    const group = autoplayGroups.get(groupId);

    if (!group) {
        return;
    }

    const activeOwnerIds = resolveActiveOwnerIds(groupId);

    for (const [ownerId, member] of group.entries()) {
        member.subscriber(activeOwnerIds.has(ownerId));
    }
}

export function registerAutoplayCandidate(
    groupId: string,
    ownerId: string,
    priority: number,
    subscriber: AutoplaySubscriber,
) {
    const group = getOrCreateGroup(groupId);

    group.set(ownerId, {
        priority,
        visible: false,
        subscriber,
    });

    notifyGroup(groupId);

    return () => {
        const currentGroup = autoplayGroups.get(groupId);

        if (!currentGroup) {
            return;
        }

        currentGroup.delete(ownerId);

        if (currentGroup.size === 0) {
            autoplayGroups.delete(groupId);
            return;
        }

        notifyGroup(groupId);
    };
}

export function updateAutoplayCandidateVisibility(
    groupId: string,
    ownerId: string,
    isVisible: boolean,
) {
    const group = autoplayGroups.get(groupId);
    const member = group?.get(ownerId);

    if (!member) {
        return;
    }

    member.visible = isVisible;
    notifyGroup(groupId);
}

export function updateAutoplayCandidatePriority(
    groupId: string,
    ownerId: string,
    priority: number,
) {
    const group = autoplayGroups.get(groupId);
    const member = group?.get(ownerId);

    if (!member || member.priority === priority) {
        return;
    }

    member.priority = priority;
    notifyGroup(groupId);
}

export function resetAutoplayRegistryForTests() {
    autoplayGroups.clear();
}
